/**
 * The image viewer's state (lifted out of PreviewContent's ImageRenderer unchanged, PV5-8:C part 1, 2026-10-13):
 * zoom by the wheel and the buttons (0.2× to 8×), a quarter turn at a time, a drag that pans, a reset, and whether
 * the browser could decode the picture at all. The renderers draw what this answers.
 *
 * Decode failure (2026-09-12): `resolvePreviewKind` classifies every `image/*` as 'image', which is right
 * for the tile's purposes and right on the platform where the format is native — an iPhone photo is HEIC, and
 * Safari paints it perfectly. Chrome and Windows cannot, so the renderer falls back to the app's own card when
 * the <img> fails to decode (a decode-time fallback rather than a classifier change: the classifier is pure, it
 * cannot know which codecs the running browser has).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface ImageViewerView {
  /** The browser could not decode the picture: draw the fallback card. */
  failed: boolean;
  zoom: number;
  rotation: number;
  pan: { x: number; y: number };
  /** A drag in progress (the picture follows the pointer without easing). */
  dragging: boolean;
  /** The CSS transform the picture is drawn with. */
  transform: string;
  onWheel: (e: React.WheelEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onImageError: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  rotate: () => void;
  reset: () => void;
}

export function useImageViewer(src: string): ImageViewerView {
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => Math.min(8, Math.max(0.2, z * (e.deltaY < 0 ? 1.12 : 0.9))));
  }, []);
  // A new source deserves a fresh attempt — a re-mint or a resolver rung that
  // lands later must not inherit the previous source's failure.
  useEffect(() => { setFailed(false); }, [src]);
  return {
    failed,
    zoom,
    rotation,
    pan,
    dragging: drag.current != null,
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
    onWheel,
    onMouseDown: (e) => { drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; },
    onMouseMove: (e) => {
      if (drag.current) setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
    },
    onMouseUp: () => { drag.current = null; },
    onMouseLeave: () => { drag.current = null; },
    onImageError: () => setFailed(true),
    zoomIn: () => setZoom((z) => Math.min(8, z * 1.25)),
    zoomOut: () => setZoom((z) => Math.max(0.2, z * 0.8)),
    rotate: () => setRotation((r) => (r + 90) % 360),
    reset: () => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); },
  };
}
